// Cover through (migration 061; Mitch, 2026-11-03): how far ahead a person is buying, typed as days ("90", "12w",
// "30*3": the same sums as number boxes, shared/calc.ts) or a date ("Dec 31", "12/31", "2026-12-31", "today"), and a
// date can add or take away days ("Dec 31 + 14", "Jan 15 - 1w"). Days stay days (90 days is 90 from whichever day it
// is); a date stays that date. Either shows as both: "Dec 31 · 58 days".
import { calc } from "./calc.ts";
import { addDays } from "./day.ts";

export type Cover = { days: number; through?: undefined } | { through: string; days?: undefined };
/** Up to two years ahead. */
export const MAX_COVER_DAYS = 730;

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const dayNo = (iso: string) => Math.round(Date.parse(`${iso}T00:00:00Z`) / 86_400_000);
const iso = (y: number, m: number, d: number) => {
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d ? t.toISOString().slice(0, 10) : null;
};
/** A month and day with no year: its next one from today (today itself counts). */
const next = (m: number, d: number, today: string) => {
  const y = Number(today.slice(0, 4));
  const day = iso(y, m, d);
  if (!day) return iso(y + 1, m, d);
  return day >= today ? day : iso(y + 1, m, d);
};
const year = (y: string) => (y.length === 2 ? 2000 + Number(y) : Number(y));

/** A date on its own, or null. */
function dateOf(text: string, today: string): string | null {
  const t = text.trim().toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ");
  if (t === "today") return today;
  if (t === "tomorrow") return addDays(today, 1);
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) return iso(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?$/.exec(t);
  if (m) return m[3] ? iso(year(m[3]), Number(m[1]), Number(m[2])) : next(Number(m[1]), Number(m[2]), today);
  // "Dec 31", "December 31 2026", "31 Dec", "31 December 2026"
  m = /^([a-z]{3,9})\.? (\d{1,2})(?:st|nd|rd|th)?(?: (\d{4}))?$/.exec(t) ?? null;
  let month = m ? m[1] : "";
  let d = m ? m[2] : "";
  let y = m ? m[3] : undefined;
  if (!m) {
    const r = /^(\d{1,2})(?:st|nd|rd|th)? ([a-z]{3,9})\.?(?: (\d{4}))?$/.exec(t);
    if (!r) return null;
    [d, month, y] = [r[1], r[2], r[3]];
  }
  const mo = MONTHS.indexOf(month.slice(0, 3)) + 1;
  if (!mo || (month.length > 3 && !"january february march april may june july august september october november december".includes(month))) return null;
  return y ? iso(Number(y), mo, Number(d)) : next(mo, Number(d), today);
}

/** "90", "12w", "30*3", "2 weeks": a number of days, or null. */
function daysOf(text: string): number | null {
  const t = text.trim().toLowerCase();
  const m = /^(.*?)\s*(d|days?|w|wks?|weeks?)?$/.exec(t);
  if (!m || !m[1]) return null;
  const n = calc(m[1]);
  if (n == null) return null;
  return m[2]?.startsWith("w") ? n * 7 : n;
}

/**
 * What a cover box's text means: `{ days }` or `{ through }`, `{ error }` when it can't be read, null when it's empty
 * (back to the plan's usual).
 */
export function parseCover(text: string, today: string): Cover | { error: string } | null {
  const t = text.trim();
  if (!t) return null;
  // A date first ("12/31" is a day, not a sum), then a date with days added or taken away ("Dec 31 + 14"; "today + 90"
  // is 90 days), then days.
  const date = dateOf(t, today);
  if (date) return checked({ through: date }, today);
  // The first + or − with a date before it ("2026-12-31 - 1w": not the dashes inside the date).
  for (const m of t.matchAll(/\s*([+\-−–])\s*/g)) {
    const left = t.slice(0, m.index);
    const base = dateOf(left, today);
    if (!base) continue;
    const rest = t.slice((m.index ?? 0) + m[0].length);
    const n = daysOf(rest);
    if (n == null) return { error: `"${rest.trim()}" isn't a number of days` };
    const shift = Math.round(m[1] === "+" ? n : -n);
    if (/^today$/i.test(left.trim())) return checked({ days: shift }, today);
    return checked({ through: addDays(base, shift) }, today);
  }
  const days = daysOf(t);
  if (days != null) return checked({ days: Math.round(days) }, today);
  return { error: 'Type days ("90", "12w") or a date ("Dec 31", "12/31/2026"), with days added or taken away if you like ("Dec 31 + 14")' };
}

function checked(c: Cover, today: string): Cover | { error: string } {
  const days = coverDays(c, today);
  if (days < 1) return { error: "Pick a day after today" };
  if (days > MAX_COVER_DAYS) return { error: "Up to two years ahead" };
  return c;
}

/** Days from today to the day it covers through. */
export const coverDays = (c: Cover, today: string) => (c.through ? dayNo(c.through) - dayNo(today) : (c.days as number));

/** The day it covers through. */
export const coverDate = (c: Cover, today: string) => (c.through ? c.through : addDays(today, c.days as number));

/** A stored row as a Cover (plan_cover: days or through). */
export const coverOf = (row: { days: number | null; through: string | null } | undefined): Cover | null =>
  !row ? null : row.through ? { through: row.through.slice(0, 10) } : row.days ? { days: row.days } : null;

/** "Dec 31", "Jan 15, 2027" (another year). */
export function coverDay(iso: string, today: string): string {
  const sameYear = iso.slice(0, 4) === today.slice(0, 4);
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  });
}

/** "Dec 31 · 58 days" (a date), "90 days · Feb 1" (days); a past date says so. */
export function coverLabel(c: Cover, today: string): string {
  const date = coverDay(coverDate(c, today), today);
  const n = coverDays(c, today);
  if (n < 1) return `${date} (passed)`;
  const days = `${n} day${n === 1 ? "" : "s"}`;
  return c.through ? `${date} · ${days}` : `${days} · ${date}`;
}
